import { useState } from "react";
import { X, MoreHorizontal, Tag, SlidersHorizontal, ReceiptText, RotateCcw, Wallet, Boxes, ShoppingCart, ShieldCheck, BarChart3, Users, FileSignature, Smartphone, LayoutDashboard, Truck, Headphones, PackageCheck, Handshake, TrendingUp, Menu, ClipboardCheck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

// Mapa de iconos por id de módulo (reutiliza la iconografía existente del proyecto).
const icons = { inicio: LayoutDashboard, approvals: ClipboardCheck, prices: Tag, reference: Tag, rules: SlidersHorizontal, quotes: ReceiptText, "manual-tradein": RotateCcw, purchases: Wallet, inventory: Boxes, "new-iphones": Smartphone, accessories: Headphones, shipments: PackageCheck, withdrawals: Truck, "simple-sale": ShoppingCart, warranty: ShieldCheck, sales: Wallet, reports: BarChart3, users: Users, contract: FileSignature, consignadores: Handshake, investments: TrendingUp };

// Categorías del menú (orden e items). Solo se muestran las que tengan módulos permitidos.
const CATEGORIES = [
  { name: "OPERACIONES", ids: ["inicio", "approvals", "manual-tradein", "purchases", "simple-sale", "withdrawals"] },
  { name: "INVENTARIO", ids: ["new-iphones", "accessories", "inventory", "shipments"] },
  { name: "GESTIÓN", ids: ["quotes", "sales", "warranty", "reports"] },
  { name: "CONFIGURACIÓN", ids: ["prices", "reference", "rules", "users", "contract"] },
];

// Accesos principales de la barra inferior móvil (máx 5 con "Más"). Respetan permisos:
// solo se muestran si el id está presente en `items` (misma lógica de visibilidad existente).
const BOTTOM = [
  { id: "inicio", label: "Inicio", icon: LayoutDashboard },
  { id: "manual-tradein", label: "Trade-In", icon: RotateCcw },
  { id: "simple-sale", label: "Venta", icon: ShoppingCart },
];

// ÚNICA fuente de navegación: deriva sidebar desktop, bottom nav móvil y "Más" de los
// mismos `items` ya filtrados por permisos/roles. No duplica listas de navegación.
export const AdminNavigation = ({ items, value, onChange }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const byId = Object.fromEntries(items.map((it) => [it[0], it]));
  const current = byId[value]?.[1];
  const go = (id) => { onChange(id); setMoreOpen(false); };

  // Agrupa en categorías; los ids no categorizados (p.ej. inversionistas/consignadores) van a OTROS.
  const buildGroups = (exclude = new Set()) => {
    const used = new Set();
    const groups = CATEGORIES.map((cat) => ({
      name: cat.name,
      items: cat.ids.map((id) => byId[id]).filter(Boolean).filter(([id]) => !exclude.has(id)),
    })).filter((g) => g.items.length);
    groups.forEach((g) => g.items.forEach(([id]) => used.add(id)));
    const rest = items.filter(([id]) => !used.has(id) && !exclude.has(id));
    if (rest.length) groups.push({ name: "OTROS", items: rest });
    return groups;
  };
  const sidebarGroups = buildGroups();

  const primary = BOTTOM.filter((b) => byId[b.id]);
  const primaryIds = new Set(primary.map((b) => b.id));
  const moreGroups = buildGroups(primaryIds);
  const hasMore = moreGroups.some((g) => g.items.length);
  const moreActive = !primaryIds.has(value);

  const SideItem = ([id, label]) => {
    const Icon = icons[id] || Menu;
    const active = value === id;
    return (
      <button key={id} type="button" data-testid={`tab-${id}`} aria-current={active ? "page" : undefined} onClick={() => go(id)}
        className={`flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-left text-[15px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${active ? "bg-slate-800 font-semibold text-white" : "text-slate-300 hover:bg-slate-900 hover:text-white"}`}>
        <Icon className={`h-5 w-5 shrink-0 ${active ? "text-emerald-400" : "text-slate-400"}`} />
        <span className="truncate">{label}</span>
      </button>
    );
  };

  const SheetItem = ([id, label]) => {
    const Icon = icons[id] || Menu;
    const active = value === id;
    return (
      <button key={id} type="button" data-testid={`more-tab-${id}`} aria-current={active ? "page" : undefined} onClick={() => go(id)}
        className={`flex min-h-[52px] w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-[15px] font-medium transition-colors ${active ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-700 active:bg-slate-100"}`}>
        <Icon className={`h-5 w-5 shrink-0 ${active ? "text-emerald-400" : "text-slate-500"}`} />
        <span className="truncate">{label}</span>
      </button>
    );
  };

  return (
    <>
      {/* ===== DESKTOP: SIDEBAR PERSISTENTE ===== */}
      <aside data-testid="admin-sidebar" className="hidden md:flex fixed left-0 top-16 bottom-0 z-30 w-64 flex-col border-r border-slate-800/80 bg-[#0F1115]">
        <nav aria-label="Módulos del panel" data-testid="admin-sidebar-nav" className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-3 py-5">
          {sidebarGroups.map((g) => (
            <div key={g.name} className="space-y-1">
              <p className="px-3.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{g.name}</p>
              {g.items.map(SideItem)}
            </div>
          ))}
        </nav>
      </aside>

      {/* Título del módulo actual (visible en móvil para orientar al navegar desde "Más") */}
      {current && value !== "inicio" && (
        <h2 className="md:hidden mb-4 min-w-0 truncate text-xl font-semibold tracking-tight text-slate-900" data-testid="admin-current-module">{current}</h2>
      )}

      {/* ===== MÓVIL: BARRA DE NAVEGACIÓN INFERIOR FIJA ===== */}
      <nav data-testid="admin-bottom-nav" aria-label="Navegación principal"
        className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_12px_rgba(0,0,0,0.05)]">
        <div className="flex">
          {primary.map((b) => {
            const active = value === b.id;
            return (
              <button key={b.id} type="button" data-testid={`bottom-nav-${b.id}`} aria-current={active ? "page" : undefined} onClick={() => go(b.id)}
                className={`flex min-h-[58px] flex-1 flex-col items-center justify-center gap-0.5 px-1 transition-colors ${active ? "text-slate-900" : "text-slate-400"}`}>
                <b.icon className={`h-5 w-5 ${active ? "text-emerald-600" : "text-slate-400"}`} />
                <span className="text-[10px] font-semibold uppercase tracking-wide">{b.label}</span>
              </button>
            );
          })}
          {hasMore && (
            <button type="button" data-testid="bottom-nav-more" aria-current={moreActive ? "page" : undefined} onClick={() => setMoreOpen(true)}
              className={`flex min-h-[58px] flex-1 flex-col items-center justify-center gap-0.5 px-1 transition-colors ${moreActive ? "text-slate-900" : "text-slate-400"}`}>
              <MoreHorizontal className={`h-5 w-5 ${moreActive ? "text-emerald-600" : "text-slate-400"}`} />
              <span className="text-[10px] font-semibold uppercase tracking-wide">Más</span>
            </button>
          )}
        </div>
      </nav>

      {/* ===== MÓVIL: "MÁS" — BOTTOM SHEET ===== */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" data-testid="admin-more-sheet"
          className="max-h-[85dvh] rounded-t-2xl border-slate-200 bg-white p-0 [&>button]:hidden">
          <SheetHeader className="flex flex-row items-center justify-between border-b border-slate-100 px-5 py-4 text-left">
            <SheetTitle className="text-base font-bold tracking-tight text-slate-900">Más opciones</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" data-testid="more-sheet-close" aria-label="Cerrar" className="h-9 w-9 rounded-xl text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></Button>
            </SheetClose>
          </SheetHeader>
          <div className="max-h-[calc(85dvh-64px)] space-y-5 overflow-y-auto overscroll-contain px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-4" data-testid="more-sheet-body">
            {moreGroups.map((g) => (
              <div key={g.name} className="space-y-1.5">
                <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g.name}</p>
                {g.items.map(SheetItem)}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
